/**
 * AuthContext — Provides auth state and actions to the entire app.
 *
 * Wrap your app (or router) with <AuthProvider> and consume via useAuthContext().
 *
 * Example:
 *   const { user, isAuthenticated, signIn, signOut } = useAuthContext();
 */

import React, { createContext, useContext } from "react";
import { useAuth, type AuthState, type AuthActions } from "../hooks/useAuth";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within an <AuthProvider>");
  }
  return ctx;
}
